import React, { useEffect, useState } from 'react';
import { Card, Image, Row, Col, Spin, Button, Typography, message, Grid } from 'antd';
import { HeartOutlined, HeartFilled, MessageOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS, API_LIKE_ENDPOINT } from './config';
import CommentsSection from './CommentsSection.js';

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

const PostGalleryWithDescription = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [likeAnim, setLikeAnim] = useState({});
  const [showCommentsFor, setShowCommentsFor] = useState({});
  const screens = useBreakpoint();

  // Fetch all posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.LIST_POSTS);
      if (Array.isArray(res.data)) setPosts(res.data);
      else setPosts([]);
    } catch (err) {
      console.error('Error fetching posts:', err);
      message.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleLike = async (postId) => {
    if (likedPosts[postId]) return;

    setLikedPosts((prev) => ({ ...prev, [postId]: true }));
    setLikeAnim((prev) => ({ ...prev, [postId]: true }));
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, like_count: (p.like_count || 0) + 1 } : p
      )
    );

    try {
      const response = await axios.post(API_LIKE_ENDPOINT, { blog_id: postId });
      if (response.data?.like_count !== undefined) {
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId ? { ...p, like_count: response.data.like_count } : p
          )
        );
      }
      message.success('Thanks for your like!');
    } catch (err) {
      console.error(err);
      setLikedPosts((prev) => ({ ...prev, [postId]: false }));
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, like_count: Math.max((p.like_count || 1) - 1, 0) } : p
        )
      );
      message.error('Failed to like. Try again.');
    } finally {
      setTimeout(() => {
        setLikeAnim((prev) => ({ ...prev, [postId]: false }));
      }, 800);
    }
  };

  if (loading) return <Spin tip="Loading posts..." style={{ display: 'block', margin: '50px auto' }} />;

  return (
    <div style={{ padding: screens.xs ? 12 : 24 }}>
      <Card title="Post Gallery" bordered={false}>
        <Row gutter={[16, 16]}>
          {posts.map((post) => (
            <Col key={post.id} xs={24} sm={12} md={8} lg={6}>
              {post.image_url ? (
                <Image
                  src={post.image_url}
                  alt={post.name || 'Post'}
                  width="100%"
                  style={{ borderRadius: 8 }}
                  preview={{ src: post.image_url }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    paddingTop: '75%',
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8,
                  }}
                >
                  <Text type="secondary">No Image</Text>
                </div>
              )}

              {/* Description */}
              {post.description && (
                <Paragraph
                  ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
                  style={{ marginTop: 8 }}
                >
                  {post.description}
                </Paragraph>
              )}

              {/* Like & Comment Buttons */}
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  type="text"
                  icon={likedPosts[post.id] ? <HeartFilled /> : <HeartOutlined />}
                  onClick={() => handleLike(post.id)}
                  className={likeAnim[post.id] ? 'animate-like' : ''}
                >
                  {post.like_count || 0}
                </Button>

                <Button
                  type="text"
                  icon={<MessageOutlined />}
                  onClick={() =>
                    setShowCommentsFor((prev) => ({
                      ...prev,
                      [post.id]: !prev[post.id],
                    }))
                  }
                >
                  {showCommentsFor[post.id] ? 'Hide' : 'Comments'}
                </Button>
              </div>

              {/* Comments Section */}
              {showCommentsFor[post.id] && (
                <div style={{ marginTop: 10 }}>
                  <CommentsSection productId={post.id} />
                </div>
              )}
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );
};

export default PostGalleryWithDescription;
